/**
 * Funding & Withdrawals view — phase 6 of broker integration.
 *
 * Lets users deposit into or withdraw from a linked broker account via the
 * supported rails (NIBSS NIP / NEFT, Paystack card, bank direct-debit,
 * internal broker sweep). Surfaces live fees, daily-limit usage, and a
 * history panel of recent transfers with status chips.
 *
 * Flow:
 *   1. Pick direction (Deposit / Withdrawal)
 *   2. Pick broker (from the link registry)
 *   3. Pick rail (validated by direction + per-rail min/max)
 *   4. Enter amount -> live fee breakdown
 *   5. Validate -> red blockers, amber warnings
 *   6. Submit (MFA-gated) -> BAL /v1/funding/transfer -> idempotent -> audit
 */
import { useMemo, useState } from 'react';
import { RAILS, computeFee, validateTransfer, seedDemoTransfers, railById, DAILY_LIMIT_KOBO,
         type Direction, type Rail, type Transfer } from '../lib/funding';
import { BROKERS, brokerById, type BrokerId } from '../lib/brokers';
import { koboToNaira, newId } from '../lib/format';

export function FundingView() {
  const [direction, setDirection] = useState<Direction>('DEPOSIT');
  const [brokerId, setBrokerId] = useState<BrokerId>('bamboo');
  const [rail, setRail] = useState<Rail>('NIBSS_INSTANT');
  const [amountNaira, setAmountNaira] = useState<string>('100000');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | undefined>();
  const [transfers, setTransfers] = useState<Transfer[]>(() => seedDemoTransfers());

  const amountKobo = useMemo(() => {
    const n = Number(amountNaira);
    if (!Number.isFinite(n) || n <= 0) return 0n;
    return BigInt(Math.round(n * 100));
  }, [amountNaira]);

  // Demo daily-used: dollar-sum of today's transfers in the chosen direction
  const dailyUsed = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return transfers
      .filter((t) => t.direction === direction && t.initiatedAt.startsWith(today) && t.status !== 'FAILED')
      .reduce((a, t) => a + BigInt(t.amountKobo), 0n);
  }, [transfers, direction]);

  // Demo available cash (fictional balance)
  const availableCash = 5_000_000_00n;

  const fee = useMemo(() => {
    if (amountKobo <= 0n) return undefined;
    try { return computeFee(rail, amountKobo, direction); } catch { return undefined; }
  }, [rail, amountKobo, direction]);
  const validation = useMemo(() => validateTransfer({
    direction, rail, amountKobo,
    availableCashKobo: direction === 'WITHDRAWAL' ? availableCash : undefined,
    dailyUsedKobo: dailyUsed,
  }), [direction, rail, amountKobo, dailyUsed]);

  const submit = async () => {
    if (!validation.ok) return;
    setSubmitting(true);
    try {
      // Simulate an async BAL /v1/funding/transfer call
      await new Promise((r) => setTimeout(r, 600));
      const spec = railById(rail)!;
      const tx: Transfer = {
        id: newId('tx'), direction, rail, brokerId,
        amountKobo: amountKobo.toString(),
        feeKobo: fee?.feeKobo.toString() ?? '0',
        status: spec.instant ? 'PROCESSING' : 'PENDING',
        initiatedAt: new Date().toISOString(),
        reference: 'REF-' + Math.random().toString(36).slice(2, 10).toUpperCase(),
      };
      setTransfers((list) => [tx, ...list]);
      setToast('Transfer initiated. Reference: ' + tx.reference);
      setTimeout(() => setTransfers((list) => list.map((x) => x.id === tx.id
        ? { ...x, status: 'SETTLED', settlesAt: new Date().toISOString() } : x)), 2500);
    } finally {
      setSubmitting(false);
    }
  };

  const railSpec = railById(rail)!;
  const limit = DAILY_LIMIT_KOBO[direction];
  const usedPct = Number((dailyUsed * 100n) / limit);

  return (
    <div className="fd">
      <div className="fd-head">
        <h2>Funding &amp; Withdrawals</h2>
        <div className="fd-sub">Move NGN into or out of your linked broker accounts. MFA is required for every transfer; every transfer is audit-logged to S3 Object-Lock (7-year retention).</div>
      </div>

      <div className="fd-grid">
        <section className="fd-card">
          <h3 className="fd-h">New Transfer</h3>

          <label className="fd-label">Direction</label>
          <div className="fd-side">
            {(['DEPOSIT', 'WITHDRAWAL'] as const).map((d) => (
              <button key={d}
                className={'fd-side-btn ' + (direction === d ? 'active ' : '') + d.toLowerCase()}
                onClick={() => setDirection(d)}>{d}</button>
            ))}
          </div>

          <label className="fd-label">Broker</label>
          <select className="te-input" value={brokerId} onChange={(e) => setBrokerId(e.target.value as BrokerId)}>
            {BROKERS.map((b) => <option key={b.id} value={b.id}>{b.logoGlyph} {b.label}</option>)}
          </select>

          <label className="fd-label">Rail</label>
          <div className="fd-rails">
            {RAILS.map((r) => (
              <button key={r.id} type="button" className={'fd-rail ' + (rail === r.id ? 'active' : '')} onClick={() => setRail(r.id)}>
                <span className="fd-rail-glyph">{r.glyph}</span>
                <span className="fd-rail-name">{r.label}</span>
                <span className="fd-rail-instant">{r.instant ? 'Instant' : 'Batch'}</span>
              </button>
            ))}
          </div>
          <div className="fd-hint">{railSpec.description}</div>

          <label className="fd-label">Amount (\u20A6)</label>
          <input className="te-input" inputMode="numeric"
            value={amountNaira}
            onChange={(e) => setAmountNaira(e.target.value.replace(/[^\d]/g, ''))} />
          <div className="fd-hint">
            Min {koboToNaira(railSpec.minKobo.toString())} &middot;
            Max {koboToNaira(railSpec.maxKobo.toString())}
            {direction === 'WITHDRAWAL' && (<> &middot; Available cash {koboToNaira(availableCash.toString())}</>)}
          </div>

          {fee && (
            <div className="fd-fee">
              <div className="fd-fee-row"><span>Amount</span><span className="mono">{koboToNaira(fee.amountKobo.toString())}</span></div>
              <div className="fd-fee-row"><span>Rail fee</span><span className="mono down">{koboToNaira(fee.feeKobo.toString())}</span></div>
              <div className="fd-fee-row"><span>{direction === 'WITHDRAWAL' ? 'Net to bank' : 'Credited to broker'}</span><span className="mono up">{koboToNaira(fee.netKobo.toString())}</span></div>
            </div>
          )}

          <div className="fd-limit">
            <div className="fd-limit-bar"><span style={{ width: Math.min(100, usedPct) + '%' }} /></div>
            <div className="fd-limit-text">Daily {direction.toLowerCase()} limit: {koboToNaira(dailyUsed.toString())} / {koboToNaira(limit.toString())} used</div>
          </div>

          {validation.errors.map((e, i) => (
            <div key={'e' + i} className="fd-err">{e}</div>
          ))}
          {validation.warnings.map((w, i) => (
            <div key={'w' + i} className="fd-warn">{w}</div>
          ))}

          <button className="fd-submit" disabled={!validation.ok || submitting || amountKobo === 0n} onClick={submit}>
            {submitting ? 'Initiating\u2026' : `${direction === 'DEPOSIT' ? 'Deposit' : 'Withdraw'} \u20A6${(Number(amountKobo) / 100).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`}
          </button>
          <div className="fd-mfa">Requires MFA &middot; SEC 2018 Rule 10(3)</div>
        </section>

        <section className="fd-card fd-history">
          <h3 className="fd-h">Recent Transfers</h3>
          <div className="fd-hist-list">
            {transfers.map((t) => (
              <div key={t.id} className={'fd-hist-row status-' + t.status.toLowerCase()}>
                <div className="fd-hist-l">
                  <div className="fd-hist-dir">{t.direction === 'DEPOSIT' ? '\u2193 DEPOSIT' : '\u2191 WITHDRAWAL'}</div>
                  <div className="fd-hist-broker">{brokerById(t.brokerId as BrokerId)?.label} &middot; {railById(t.rail)?.label}</div>
                  {t.reference && <div className="fd-hist-ref">Ref: {t.reference}</div>}
                  {t.reason && <div className="fd-hist-reason">Failure: {t.reason}</div>}
                </div>
                <div className="fd-hist-m mono">{koboToNaira(t.amountKobo)}
                  {Number(t.feeKobo) > 0 && <span className="fd-hist-fee"> (fee {koboToNaira(t.feeKobo)})</span>}
                </div>
                <div className="fd-hist-r">
                  <span className={'fd-status fd-' + t.status.toLowerCase()}>{t.status}</span>
                  <div className="fd-hist-time">{new Date(t.initiatedAt).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            ))}
            {transfers.length === 0 && <div className="bl-empty">No transfers yet.</div>}
          </div>
        </section>
      </div>

      {toast && <div className="bs-toast">{toast}</div>}
    </div>
  );
}
